import { useState, useEffect } from 'react';
import { useFormState, useFieldArray, type Control } from 'react-hook-form';
import { Button, Divider, Label } from '@schibsted-svp/react-ui';
import type { Asset } from '@schibsted-svp/svp-api-types';
import { getDuration, isAudio } from 'models/asset';
import { unique } from 'lib/array';
import { usePlayer } from 'hooks/usePlayer';
import FormattedTime from 'components/core/FormattedTime';
import type { FormValues } from 'components/AssetForm/types';
import config from 'config';
import { MdInfoOutline } from 'react-icons/md';
import { useCategoryWatch } from 'components/AssetForm/hooks/useCategoryWatch';
import { stringToNumber } from 'lib/number';
import { MidrollsForm } from './MidrollsForm';

import css from './Midrolls.module.scss';

export type Midroll = {
    timeline: number;
};

type MidrollsProps = {
    asset: Asset;
    control: Control<FormValues>;
};

const AD_BREAKS_LIMIT = config.podcasts.adBreaksLimit;

const getSlotName = (index: number, array: unknown[]) => {
    if (index === 0) {
        return 'preroll';
    }

    if (array.length !== 2 && index === array.length - 1) {
        return 'postroll';
    }
    return 'midroll';
};

export function Midrolls({ asset, control }: MidrollsProps) {
    const { isSubmitting, dirtyFields } = useFormState({ control });
    const { fields, insert, remove } = useFieldArray({ control, name: 'additional.cuePoints' });

    const { category } = useCategoryWatch({ provider: asset.provider, control });
    const categoryMetadata = category?.additional?.metadata;
    const automaticAds = Number(categoryMetadata?.podcast_automaticAds?.value || 0);
    const customPrerollPosition = stringToNumber(categoryMetadata?.podcast_customPrerollPosition?.value);
    const customPostrollPosition = stringToNumber(categoryMetadata?.podcast_customPostrollPosition?.value);

    const breakSpan = getDuration(asset) / (automaticAds + 1);

    const isCuePointsDirty = dirtyFields?.additional?.cuePoints?.some(Boolean);
    const player = usePlayer();

    const [isManualAdBreaksOn, setIsManualAdBreaksOn] = useState(!automaticAds);
    const isLimitReached = fields.length >= AD_BREAKS_LIMIT;

    const excluded = unique(fields.map((item) => item.timeline));

    useEffect(() => {
        setIsManualAdBreaksOn(!automaticAds);
    }, [automaticAds]);

    const onMidrollAdd = (item: Midroll) => {
        const index = fields.filter(({ timeline }) => timeline < item.timeline).length;
        insert(index, item);
    };

    const onMidrollRemove = (index: number) => {
        remove(index);
    };

    const overwriteAdBreaks = () => {
        setIsManualAdBreaksOn(true);
    };

    let adBreaks = [];
    const shouldShowDefaultAdBreaks = automaticAds && !isManualAdBreaksOn && !fields.length;

    if (shouldShowDefaultAdBreaks) {
        adBreaks = Array.from(Array(automaticAds)).map((_, index, array) => {
            const slot = getSlotName(index, array);
            let timeline = breakSpan * (index + 1);
            if (slot === 'preroll' && customPrerollPosition !== undefined) {
                timeline = Math.floor(getDuration(asset) * customPrerollPosition);
            }
            if (slot === 'postroll' && customPostrollPosition !== undefined) {
                timeline = Math.floor(getDuration(asset) * customPostrollPosition);
            }
            return { slot, timeline };
        });
    }

    return (
        <section>
            {shouldShowDefaultAdBreaks ? (
                <div className={css.automaticAds}>
                    <div className={css.info}>
                        <MdInfoOutline size={25} className={css.icon} />
                        <span>
                            <p className={css.adText}>Automatic ads are on and set to the following times:</p>
                            <p className={css.overwriteText}>
                                (You can overwrite them by clicking “Overwrite ad breaks”){' '}
                            </p>
                        </span>
                    </div>
                    {adBreaks.map(({ slot, timeline }) => (
                        <div key={slot} className={css.slot}>
                            <Label>{slot}</Label>
                            <span>
                                Start time:{' '}
                                <FormattedTime
                                    onClick={(time: number) => {
                                        player?.seek(time);
                                    }}
                                    seconds={timeline}
                                />
                            </span>
                            <Divider className={css.divider} margin="0 0" />
                        </div>
                    ))}
                    <Button variant="standard" size="small" className={css.overwrite} onClick={overwriteAdBreaks}>
                        {' '}
                        Overwrite ad breaks{' '}
                    </Button>
                </div>
            ) : (
                <MidrollsForm
                    duration={getDuration(asset)}
                    excluded={excluded}
                    submitDisabled={isSubmitting}
                    onMidrollAdd={onMidrollAdd}
                    isLimitReached={isLimitReached}
                    isAudio={isAudio(asset)}
                    isCuePointsDirty={isCuePointsDirty}
                />
            )}
            {fields && (
                <ul className={css.list}>
                    {fields.map(({ id, timeline }, index) => (
                        <li key={`item:${id}:${timeline}`}>
                            <span>
                                Start time:{' '}
                                <FormattedTime
                                    onClick={(time: number) => {
                                        player?.seek(time);
                                    }}
                                    seconds={timeline}
                                />
                            </span>
                            <Button
                                size="compact"
                                variant="standard"
                                type="button"
                                onClick={() => onMidrollRemove(index)}
                            >
                                Delete
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
